import { Suspense } from 'react';
import { RouterProvider, type RouterProviderProps } from 'react-router';

import { RootErrorBoundary } from '@/components/error-boundary/root-error-boundary';
import { DialogProvider } from '@/components/providers/dialog-provider';
import { QueryProvider } from '@/components/providers/query-provider';
import { ThemeProvider } from '@/components/providers/theme-provider';
import { FlagSyncLoader } from '@/components/svgs/logo';
import { TailwindIndicator } from '@/components/tailwind-indicator';
import { Toaster } from '@/components/ui/toaster';
import { TooltipProvider } from '@/components/ui/tooltip';
import { AppEnvMap, AppEnvironment } from '@/core/app-env';

function AppFallback() {
  return (
    <div className="flex h-screen items-center justify-center">
      <FlagSyncLoader className="h-12 w-12" />
    </div>
  );
}

/**
 * The provider tree + router rendered identically by the browser entry
 * (main.tsx) and the build-time prerender (prerender.tsx). Anything that emits
 * DOM into #app MUST live here: prerendered HTML and the client's first render
 * have to match or hydrateRoot throws a mismatch. Client-only, DOM-less
 * wrappers (StrictMode, KillSwitch, Vercel Analytics) stay in main.tsx.
 */
export function AppShell({
  router,
}: {
  router: RouterProviderProps['router'];
}) {
  return (
    <ThemeProvider
      attribute="class"
      defaultTheme="system"
      enableSystem
      disableTransitionOnChange
    >
      <DialogProvider>
        <QueryProvider>
          <TooltipProvider>
            <RootErrorBoundary>
              <Suspense fallback={<AppFallback />}>
                <RouterProvider router={router} />
              </Suspense>
            </RootErrorBoundary>
            <Toaster />
            {AppEnvMap.Env !== AppEnvironment.Prod && <TailwindIndicator />}
          </TooltipProvider>
        </QueryProvider>
      </DialogProvider>
    </ThemeProvider>
  );
}
